import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';
import ArrowUpSVG from '@birdi/icons/arrow-up.svg';
import ChevronRightSVG from '@birdi/icons/chevron-right.svg';
import FolderSVG from '@birdi/icons/folder.svg';
import WorkspaceSVG from '@birdi/icons/card-view.svg';

export interface MoveContextMenuProps {
  onMoveToWorkspace: () => void;
  onMoveToFolder: () => void;
}

export const MoveContextMenu: React.FC<MoveContextMenuProps> = ({
  onMoveToWorkspace,
  onMoveToFolder,
}) => {
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false); // Keeps submenu open after click
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);

  const SUBMENU_WIDTH = 150;
  const GAP = 2;

  // Update submenu position when opening
  useEffect(() => {
    if (isSubmenuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const screenWidth = window.innerWidth;

      // Check if there's enough space on the right
      const spaceOnRight = screenWidth - rect.right;
      const hasSpaceOnRight = spaceOnRight >= SUBMENU_WIDTH + GAP;

      setSubmenuPosition({
        top: rect.top,
        left: hasSpaceOnRight
          ? rect.right + GAP // Position on the right
          : rect.left - SUBMENU_WIDTH - GAP, // Position on the left
      });
    }
  }, [isSubmenuOpen]);

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        submenuRef.current &&
        !submenuRef.current.contains(event.target as Node)
      ) {
        setIsSubmenuOpen(false);
        setIsPinned(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => setIsSubmenuOpen(true);
  const handleMouseLeave = (e: React.MouseEvent) => {
    // Don't close if pinned by click
    if (isPinned) return;

    // Check if moving to submenu
    const relatedTarget = e.relatedTarget as Node;
    if (submenuRef.current?.contains(relatedTarget)) {
      return;
    }
    setIsSubmenuOpen(false);
  };

  const handleSubmenuMouseLeave = (e: React.MouseEvent) => {
    // Don't close if pinned by click
    if (isPinned) return;

    // Check if moving back to button
    const relatedTarget = e.relatedTarget as Node;
    if (buttonRef.current?.contains(relatedTarget)) {
      return;
    }
    setIsSubmenuOpen(false);
  };

  const handleClick = () => {
    if (isSubmenuOpen && isPinned) {
      // If already pinned and open, close it
      setIsSubmenuOpen(false);
      setIsPinned(false);
    } else {
      // Open and pin
      setIsSubmenuOpen(true);
      setIsPinned(true);
    }
  };

  const submenuContent = isSubmenuOpen && (
    <div
      ref={submenuRef}
      onMouseLeave={handleSubmenuMouseLeave}
      css={(theme: BirdiTheme) => css`
        position: fixed;
        top: ${submenuPosition.top}px;
        left: ${submenuPosition.left}px;
        min-width: 150px;
        background: ${theme.mono0};
        border-radius: 3px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
        z-index: 9999;
        overflow: hidden;
      `}
    >
      <button
        type="button"
        css={(theme: BirdiTheme) => css`
          display: flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: none;
          background: none;
          cursor: pointer;
          text-align: left;

          &:hover {
            background-color: ${theme.sand};
          }
        `}
        onClick={(e) => {
          e.stopPropagation();
          setIsSubmenuOpen(false);
          setIsPinned(false);
          onMoveToWorkspace();
        }}
      >
        <WorkspaceSVG />
        <span>Workspace</span>
      </button>
      <button
        type="button"
        css={(theme: BirdiTheme) => css`
          display: flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: none;
          background: none;
          cursor: pointer;
          text-align: left;

          &:hover {
            background-color: ${theme.sand};
          }
        `}
        onClick={(e) => {
          e.stopPropagation();
          setIsSubmenuOpen(false);
          setIsPinned(false);
          onMoveToFolder();
        }}
      >
        <FolderSVG />
        <span>Folder</span>
      </button>
    </div>
  );

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <button
        ref={buttonRef}
        type="button"
        css={(theme: BirdiTheme) => css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: none;
          background: none;
          cursor: pointer;
          text-align: left;

          &:hover {
            background-color: ${theme.sand};
          }
        `}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 0.5rem;
          `}
        >
          <ArrowUpSVG />
          <span>Move map to</span>
        </div>
        <span
          css={css`
            display: flex;
            width: 16px;
            height: 16px;
          `}
        >
          <ChevronRightSVG />
        </span>
      </button>

      {typeof document !== 'undefined' && createPortal(submenuContent, document.body)}
    </div>
  );
};
